"use client";

import { useEffect, useMemo, useState } from "react";
import type { SearchIntent } from "@/lib/search/unified-search";

export interface SearchHistoryItem {
	query: string;
	intent: SearchIntent;
	createdAt: number;
}

const STORAGE_KEY = "odin_search_history_v1";
const MAX_HISTORY_ITEMS = 12;

export function useSearchHistory() {
	const [history, setHistory] = useState<SearchHistoryItem[]>([]);
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		try {
			const raw = localStorage.getItem(STORAGE_KEY);
			if (!raw) {
				setLoaded(true);
				return;
			}

			const parsed = JSON.parse(raw) as SearchHistoryItem[];
			setHistory(Array.isArray(parsed) ? parsed : []);
		} catch {
			setHistory([]);
		} finally {
			setLoaded(true);
		}
	}, []);

	useEffect(() => {
		if (!loaded) return;
		localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
	}, [history, loaded]);

	const api = useMemo(
		() => ({
			history,
			addHistory(query: string, intent: SearchIntent) {
				const trimmed = query.trim();
				if (!trimmed || intent === "empty" || intent === "unsupported-evm") return;

				setHistory((current) => {
					const next = current.filter(
						(item) => item.query.toLowerCase() !== trimmed.toLowerCase(),
					);
					next.unshift({
						query: trimmed,
						intent,
						createdAt: Date.now(),
					});
					return next.slice(0, MAX_HISTORY_ITEMS);
				});
			},
			clearHistory() {
				setHistory([]);
			},
			removeHistory(query: string) {
				setHistory((current) =>
					current.filter((item) => item.query.toLowerCase() !== query.toLowerCase()),
				);
			},
		}),
		[history],
	);

	return api;
}
